import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import {
  Alert,
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useNotify, useStore, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { track } from '../analytics/usageTracker'
import { type AlertAccess, getAlertAccess } from '../authProvider'
import { formatGlucoseForDisplay } from '../common'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { getAlertSeverityColor } from '../types/alerts'
import { authHeaders } from '../utils/api'
import { type GlucoseUnit } from '../utils/glucoseUnits'
import {
  announceAlertAcknowledged,
  beginAcknowledge,
  endAcknowledge,
  isKnownAcknowledged,
  useAcknowledgementsFromOtherSurfaces,
  withKnownAcks,
} from './alertAckEvents'
import { glucoseDirection } from './alertGlucoseDirection'
import { fetchAlertsSince } from './alertPollWindow'
import { mergeAlertsById } from './mergeAlertsById'

// Get API base URL from environment (same as dataProvider)
const apiUrl = import.meta.env.VITE_BACKEND_API_URL || '/api'

interface SimpleAlert {
  id: number
  petId: number
  alertType: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  title: string
  message: string
  triggeredAt: string
  acknowledgedAt?: string
  glucoseValue?: number
  recommendations?: string[]
}

/**
 * Alert Menu Button for Top Navigation Bar
 *
 * Compact alert icon with badge count that opens a dropdown menu
 * Perfect for the AppBarToolbar alongside other navigation icons
 *
 * IMPORTANT: Uses the same data source as SimpleAlertPanel for consistent counts
 */
const AlertMenuButton: React.FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const navigate = useNavigate()
  const [selectedPetId] = useStore('selectedPetId')
  const [glucoseUnit] = useStore<GlucoseUnit>('glucoseUnit', 'mmol/L')

  const [alerts, setAlerts] = useState<SimpleAlert[]>([])
  // Mirror of `alerts` so the async poll handler can merge an incremental
  // batch into the CURRENT list without a stale closure (L-020).
  const alertsRef = useRef<SimpleAlert[]>([])
  const [totalUnacknowledged, setTotalUnacknowledged] = useState(0) // Server-side count of all unacknowledged alerts
  const [loading, setLoading] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  // null = still checking. 'unknown' (identity call failed) is NOT 'denied'.
  const [alertAccess, setAlertAccess] = useState<AlertAccess | null>(null)
  const hasAlertPermission = alertAccess === 'allowed'
  const [accessRetry, setAccessRetry] = useState(0)
  // P32-2: poll failures used to go to the console only, so the badge froze at
  // its last value forever (e.g. after a sleep expired the session) and looked
  // like a live all-clear. Its sibling SimpleAlertPanel always surfaced this.
  const [fetchError, setFetchError] = useState<string | null>(null)
  const lastCheckRef = useRef<Date>(new Date(Date.now() - 24 * 60 * 60 * 1000))

  useEffect(() => {
    alertsRef.current = alerts
  }, [alerts])

  // #42 (R39): another surface of this tab acknowledged an alert. The list takes
  // it (and every later merge re-applies it); the all-pets total is RE-POLLED --
  // subtracting could double-count an ack a newer poll already reflects, and a
  // count too low hides a real alert.
  const [refreshTick, setRefreshTick] = useState(0)
  useAcknowledgementsFromOtherSurfaces(setAlerts, () => {
    if (!selectedPetId) setRefreshTick((t) => t + 1)
  })

  const pollingInterval = 2 * 60 * 1000 // 2 minutes

  // Track the previous selectedPetId to detect changes
  const prevSelectedPetIdRef = useRef<any>(selectedPetId)

  // Check alert permissions on mount
  useEffect(() => {
    getAlertAccess().then((access) => {
      setAlertAccess(access)
      if (access !== 'allowed') {
        console.log(
          `AlertMenuButton: alert access = ${access} (allowed requires admin or vet role)`,
        )
      }
    })
  }, [accessRetry])

  // Reset lastCheckRef when selectedPetId changes to get fresh data for the new pet
  useEffect(() => {
    if (prevSelectedPetIdRef.current !== selectedPetId) {
      // Reset to 24 hours ago to get all recent alerts for the new pet context
      lastCheckRef.current = new Date(Date.now() - 24 * 60 * 60 * 1000)
      prevSelectedPetIdRef.current = selectedPetId
      // Clear the previous pet-context's alerts immediately (L-020) — the
      // polling effect re-runs on this same change and repopulates from the
      // new context's 24h window (~1 fetch RTT).
      setAlerts([])
      setTotalUnacknowledged(0)
    }
  }, [selectedPetId])

  // Fetch alerts from API - uses same endpoint as SimpleAlertPanel for consistency
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshTick is a deliberate re-run trigger (R39: the all-pets total is re-polled when another surface acknowledges)
  useEffect(() => {
    // Skip polling if user doesn't have alert permissions
    if (!hasAlertPermission) {
      return
    }

    // One effect run = one selectedPetId. A response that resolves after the
    // cleanup is DROPPED: its closure still filters by the previous pet, and
    // merging it would show that pet's alerts (and badge) under the newly
    // selected one until the next switch (VP-ALERTS-R07, AlertPoll.tla F1).
    let cancelled = false
    const controller = new AbortController()

    const fetchAlerts = async () => {
      try {
        setLoading(true)

        // Cookie-or-token auth (L-019): on a browser reopen sessionStorage is
        // empty but the HttpOnly payload-token cookie is still valid — fire the
        // same-origin request so the alert bell isn't silently emptied on reopen.
        // Use the same endpoint as SimpleAlertPanel for consistent data.
        const walk = await fetchAlertsSince(apiUrl, lastCheckRef.current, {
          headers: authHeaders(),
          credentials: 'same-origin',
          signal: controller.signal,
        })
        if (cancelled) return

        if (walk.ok) {
          // The walk in the legacy body shape. An INCOMPLETE walk (page cap / no
          // progress) carries no lastUpdate, so the watermark below holds and the
          // next poll re-reads (ada-core#230, AlertPoll.tla F4).
          const data = {
            success: true,
            data: {
              alerts: walk.alerts as any[],
              summary: walk.summary,
              lastUpdate: walk.complete ? walk.lastUpdate : undefined,
            },
          }

          if (data.success && data.data) {
            const { alerts: newAlerts, summary } = data.data

            // Transform alerts to our format
            const transformedAlerts: SimpleAlert[] = (newAlerts || []).map((alert: any) => {
              const petId = alert.pet_id || alert.petId || alert.pet || null

              return {
                id: alert.id || Math.random(),
                petId: petId,
                alertType: alert.alertType || alert.alert_type || 'unknown',
                severity: (alert.severity || 'medium').toLowerCase() as SimpleAlert['severity'],
                // Raw values: the fallback text is applied at RENDER so a locale switch
                // re-translates alerts already held in state.
                title: alert.title || '',
                message: alert.message || '',
                triggeredAt: alert.triggeredAt || alert.triggered_at || new Date().toISOString(),
                acknowledgedAt: alert.acknowledgedAt || alert.acknowledged_at,
                glucoseValue: alert.glucoseValue || alert.glucose_value,
                recommendations: alert.recommendations || [],
              }
            })

            // Filter by selectedPetId if specified (strict matching only)
            const filteredAlerts = selectedPetId
              ? transformedAlerts.filter((alert) => {
                  const alertPetId = alert.petId
                  const selectedPetIdNum =
                    typeof selectedPetId === 'string' ? parseInt(selectedPetId, 10) : selectedPetId
                  const alertPetIdNum =
                    typeof alertPetId === 'string' ? parseInt(alertPetId, 10) : alertPetId

                  return alertPetIdNum === selectedPetIdNum
                })
              : transformedAlerts

            // The endpoint is INCREMENTAL (alerts since lastCheck) — MERGE the
            // batch into the displayed list, never assign it (L-020: the old
            // unconditional replace meant the FIRST empty poll — ≤2 min after
            // mount, the common case — wiped the bell's list and zeroed the
            // pet-scoped badge while unacknowledged alerts still existed).
            // R39: re-apply this tab's recorded acknowledgements -- this batch may
            // predate one another surface just made.
            // FUNCTIONAL, never from alertsRef: the ref syncs in a passive effect
            // after the commit, so a response landing in between merged from the
            // list BEFORE a failed acknowledge's revert and put the refused alert
            // back to resolved for the session (AckSurfaces.tla K2).
            setAlerts((prev) => withKnownAcks(mergeAlertsById(prev, filteredAlerts, 50)))

            // No pet selected: the badge is the server's total. With a pet it
            // is derived from the list (shownUnacknowledged). A walk that hit the
            // page cap carries NO summary: keep the last total rather than show
            // 0 -- a badge too low hides real alerts. With NO total yet (the first
            // poll, or the first after a pet -> no pet switch) "the last total" is
            // the initial 0, so the unacknowledged alerts this walk actually loaded
            // are a FLOOR: raise to them, never lower (R46).
            if (!selectedPetId) {
              if (summary?.unacknowledged != null) {
                setTotalUnacknowledged(summary.unacknowledged)
              } else {
                const loaded = withKnownAcks(filteredAlerts).filter((a) => !a.acknowledgedAt).length
                setTotalUnacknowledged((prev) => Math.max(prev, loaded))
              }
            }

            // Advance the watermark ONLY when the raw batch is non-empty
            // (P63-6, unified with SimpleAlertPanel): an empty batch gains
            // nothing by advancing — the next poll costs the same — and if an
            // alert's commit lags the server's lastUpdate, advancing on empty
            // is how it gets skipped forever.
            if (transformedAlerts.length > 0 && data.data.lastUpdate) {
              lastCheckRef.current = new Date(data.data.lastUpdate)
            }
          }
          setFetchError(null)
        } else {
          const { response } = walk
          setFetchError(
            response.status === 401 || response.status === 403
              ? 'pos.alerts.fetch_error_auth'
              : 'pos.alerts.fetch_error_unavailable',
          )
          console.warn('AlertMenuButton: Failed to fetch alerts, status:', response.status)
        }
      } catch (error) {
        if (cancelled) return // our own abort on cleanup, not a feed failure
        setFetchError('pos.alerts.fetch_error_unavailable')
        console.error('AlertMenuButton: Error fetching alerts:', error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    // Initial fetch
    fetchAlerts()

    // Set up polling interval - 2 minutes
    const interval = setInterval(fetchAlerts, pollingInterval)
    return () => {
      cancelled = true
      controller.abort()
      clearInterval(interval)
    }
  }, [hasAlertPermission, selectedPetId, refreshTick]) // Re-fetch when permission or pet selection changes, or on R39's re-poll

  const getSeverityIcon = (alert: SimpleAlert) => {
    const color = getAlertSeverityColor(alert.severity)
    const iconProps = { style: { color, fontSize: 16 } }

    switch (glucoseDirection(alert.alertType)) {
      case 'down':
        return <TrendingDownIcon {...iconProps} />
      case 'up':
        return <TrendingUpIcon {...iconProps} />
      default:
        return alert.severity === 'critical' ? (
          <ErrorIcon {...iconProps} />
        ) : alert.severity === 'high' ? (
          <WarningIcon {...iconProps} />
        ) : (
          <InfoIcon {...iconProps} />
        )
    }
  }

  const getAlertTypeLabel = (type: string): string => {
    const key = `pos.alerts.type.${type}`
    const translated = translate(key)
    return translated !== key ? translated : type
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    track({ type: 'alert_list_opened', section: 'alerts' })
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleAlertClick = async (alert: SimpleAlert) => {
    // Close the dropdown first
    setAnchorEl(null)

    // Auto-acknowledge when clicking on an unacknowledged alert (silent mode)
    if (!alert.acknowledgedAt) {
      // Fire and forget - don't wait for acknowledge to complete before navigating
      acknowledgeAlertCore(alert.id, true)
    }

    // Validate petId before navigation to prevent logout issue
    if (!alert.petId || alert.petId === null || alert.petId === undefined) {
      notify(translate('pos.alerts.navigate_cannot'), { type: 'warning' })
      console.warn('AlertMenuButton: Invalid petId for alert:', alert)
      return
    }

    // Create enhanced navigation with alert context
    const alertTimestamp = new Date(alert.triggeredAt).getTime()
    const glucoseValue = alert.glucoseValue

    // Build navigation URL with alert context parameters
    const baseUrl = `/pets/${alert.petId}/show`
    const urlParams = new URLSearchParams()

    // Add alert context to URL for chart highlighting
    urlParams.set('alertId', alert.id.toString())
    urlParams.set('alertTimestamp', alertTimestamp.toString())
    urlParams.set('alertType', alert.alertType)

    if (glucoseValue) {
      urlParams.set('alertGlucose', glucoseValue.toString())
    }

    // Smart navigation based on alert type with enhanced context
    if (glucoseDirection(alert.alertType) !== null) {
      // Navigate with glucose context for chart highlighting
      urlParams.set('view', 'glucose')
      urlParams.set('highlight', 'true')
      navigate(`${baseUrl}?${urlParams.toString()}`)

      const glucoseText = glucoseValue
        ? ` (${formatGlucoseForDisplay(glucoseValue, glucoseUnit)})`
        : ''
      notify('pos.alerts.navigating_chart', {
        type: 'info',
        messageArgs: {
          time: new Date(alert.triggeredAt).toLocaleTimeString(),
          glucose: glucoseText,
        },
      })
    } else if (alert.alertType === 'pattern_detected' || alert.alertType === 'somogyi') {
      // Navigate with pattern analysis context
      urlParams.set('view', 'patterns')
      urlParams.set('timeRange', '24h') // Show 24h context for patterns
      navigate(`${baseUrl}?${urlParams.toString()}`)
      notify('pos.alerts.navigating_patterns', {
        type: 'info',
        messageArgs: { date: new Date(alert.triggeredAt).toLocaleDateString() },
      })
    } else {
      // Default navigation with alert context
      urlParams.set('view', 'overview')
      navigate(`${baseUrl}?${urlParams.toString()}`)
      notify('pos.alerts.navigating_overview', {
        type: 'info',
        messageArgs: { title: alert.title || translate('pos.alerts.fallback_title') },
      })
    }

    // Store alert context in React Admin store for chart components to use
    // This allows glucose charts to highlight the specific alert timestamp
    const alertContext = {
      alertId: alert.id,
      timestamp: alertTimestamp,
      glucoseValue: glucoseValue,
      alertType: alert.alertType,
      severity: alert.severity,
      title: alert.title,
    }

    // Store in sessionStorage for chart components to access
    sessionStorage.setItem('currentAlertContext', JSON.stringify(alertContext))
  }

  // Core acknowledge function with optional silent mode for auto-acknowledge on click
  const acknowledgeAlertCore = async (alertId: number, silent = false) => {
    // Outside the try, so the finally's endAcknowledge always has its pair (R45).
    beginAcknowledge(alertId)
    try {
      const acknowledgedAt = new Date().toISOString()
      // Optimistically update UI first
      setAlerts((prev) =>
        prev.map((alert) => (alert.id === alertId ? { ...alert, acknowledgedAt } : alert)),
      )
      // Decrement the total unacknowledged count optimistically
      setTotalUnacknowledged((prev) => Math.max(0, prev - 1))

      // Cookie-or-token auth (L-019) — acknowledge must work on a cookie-only
      // reopen. Same acknowledge endpoint as SimpleAlertPanel for consistency.
      const response = await deadlineFetch(`${apiUrl}/alert-events/acknowledge-alert`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'same-origin',
        body: JSON.stringify({
          alertId,
          notes: silent
            ? 'Auto-acknowledged on view from vetpo menubar'
            : 'Acknowledged from vetpo menubar',
        }),
      })

      if (response.ok) {
        announceAlertAcknowledged(alertId, acknowledgedAt) // #42: tell the panel
        if (!silent) {
          notify(translate('pos.alerts.acknowledge_success'), { type: 'success' })
        }
      } else {
        // Revert optimistic update if endpoint fails
        // Unless another surface's acknowledge of it already SUCCEEDED (R39):
        // the server holds it acknowledged, so do not show it unresolved again.
        if (!isKnownAcknowledged(alertId)) {
          setAlerts((prev) =>
            prev.map((alert) =>
              alert.id === alertId ? { ...alert, acknowledgedAt: undefined } : alert,
            ),
          )
          // No pet selected: RE-POLL the server's total -- a poll that landed
          // since the optimistic -1 already counts it, so +1 counted it twice (K3).
          if (!selectedPetId) setRefreshTick((t) => t + 1)
        }
        if (!silent) {
          notify(translate('pos.alerts.acknowledge_failed'), { type: 'error' })
        }
      }
    } catch (error) {
      console.error('Error acknowledging alert:', error)
      // Revert optimistic update
      // Unless another surface's acknowledge of it already SUCCEEDED (R39):
      // the server holds it acknowledged, so do not show it unresolved again.
      if (!isKnownAcknowledged(alertId)) {
        setAlerts((prev) =>
          prev.map((alert) =>
            alert.id === alertId ? { ...alert, acknowledgedAt: undefined } : alert,
          ),
        )
        // No pet selected: RE-POLL the server's total -- a poll that landed
        // since the optimistic -1 already counts it, so +1 counted it twice (K3).
        if (!selectedPetId) setRefreshTick((t) => t + 1)
      }
      if (!silent) {
        notify(translate('pos.alerts.acknowledge_failed'), { type: 'error' })
      }
    } finally {
      endAcknowledge(alertId) // after any announce: a success is already recorded
    }
  }

  // Event handler for explicit acknowledge button click
  const handleAcknowledge = async (alertId: number, event: React.MouseEvent) => {
    event.stopPropagation()
    await acknowledgeAlertCore(alertId, false)
  }

  const unacknowledgedAlerts = alerts.filter((alert) => !alert.acknowledgedAt)
  const criticalAlerts = unacknowledgedAlerts.filter((alert) => alert.severity === 'critical')
  const recentAlerts = alerts.slice(0, 10) // Show only 10 most recent

  // Determine badge color based on severity
  // What the badge shows (R39): scoped to a pet, the unacknowledged alerts in
  // the LIST -- derived, so no ±1 bookkeeping can drift from what is listed;
  // otherwise the server's total.
  const shownUnacknowledged = selectedPetId
    ? alerts.filter((alert) => !alert.acknowledgedAt).length
    : totalUnacknowledged
  const badgeColor =
    criticalAlerts.length > 0 ? 'error' : shownUnacknowledged > 0 ? 'warning' : 'default'

  // The feed is NOT live if we could not check access, or if polling is failing.
  // Rendering the ordinary bell in that state is an ambient all-clear the app
  // cannot back up (P32-1/P32-2) — the icon and tooltip must say so.
  const feedIsDegraded = alertAccess === 'unknown' || !!fetchError
  const degradedReason = fetchError ?? 'pos.alerts.access_unknown'

  return (
    <>
      <Tooltip
        title={
          feedIsDegraded
            ? translate(degradedReason)
            : translate('pos.alerts.unacknowledged_count', { count: shownUnacknowledged })
        }
      >
        <IconButton
          color="inherit"
          onClick={handleClick}
          aria-label={feedIsDegraded ? 'alerts-degraded' : 'alerts'}
          size="large"
          data-testid={feedIsDegraded ? 'alert-bell-degraded' : 'alert-bell'}
        >
          <Badge badgeContent={shownUnacknowledged} color={badgeColor} max={99}>
            {criticalAlerts.length > 0 ? (
              <ErrorIcon color="error" />
            ) : shownUnacknowledged > 0 ? (
              <WarningIcon color="warning" />
            ) : feedIsDegraded ? (
              <NotificationsOffIcon color="warning" />
            ) : alerts.length > 0 ? (
              <NotificationsIcon />
            ) : (
              <NotificationsOffIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            // Clamp to the viewport on phones so the popover doesn't clip; 400px at sm+.
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 },
            maxHeight: 500,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" component="div">
            {translate('pos.alerts.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedPetId
              ? translate('pos.alerts.pet_alerts', { petId: selectedPetId })
              : translate('pos.alerts.all_pets')}
          </Typography>
        </Box>

        {criticalAlerts.length > 0 && (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="error"
              sx={{ fontSize: '0.875rem' }}
              action={
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={(e) => criticalAlerts.forEach((alert) => handleAcknowledge(alert.id, e))}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              }
            >
              {translate('pos.alerts.critical_alerts_count', { count: criticalAlerts.length })}
            </Alert>
          </Box>
        )}

        {recentAlerts.length === 0 ? (
          <MenuItem disabled>
            <ListItemIcon>
              <NotificationsOffIcon />
            </ListItemIcon>
            <ListItemText
              primary={translate('pos.alerts.no_recent_alerts')}
              secondary={translate('pos.alerts.checking_interval', {
                minutes: Math.round(pollingInterval / 60000),
              })}
            />
          </MenuItem>
        ) : (
          recentAlerts.map((alert, index) => (
            <Box key={alert.id}>
              {index > 0 && <Divider />}
              <MenuItem
                onClick={() => handleAlertClick(alert)}
                sx={{
                  borderLeft: 4,
                  borderColor: getAlertSeverityColor(alert.severity),
                  opacity: alert.acknowledgedAt ? 0.7 : 1,
                  bgcolor: !alert.acknowledgedAt ? 'action.hover' : 'inherit',
                  cursor: 'pointer',
                }}
              >
                <ListItemIcon>{getSeverityIcon(alert)}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" noWrap>
                        {alert.title || translate('pos.alerts.fallback_title')}
                      </Typography>
                      <Chip
                        label={getAlertTypeLabel(alert.alertType)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                      {alert.glucoseValue && (
                        <Chip
                          label={formatGlucoseForDisplay(alert.glucoseValue, glucoseUnit)}
                          size="small"
                          color="primary"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {alert.message || translate('pos.alerts.fallback_message')}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(alert.triggeredAt).toLocaleString()}
                        {alert.acknowledgedAt && ` • ${translate('pos.alerts.acknowledged')}`}
                      </Typography>
                    </Box>
                  }
                />
                {!alert.acknowledgedAt && (
                  <IconButton
                    edge="end"
                    onClick={(e) => handleAcknowledge(alert.id, e)}
                    color="success"
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                )}
              </MenuItem>
            </Box>
          ))
        )}

        {alerts.length > 10 && (
          <Box sx={{ p: 1 }}>
            <Typography variant="caption" color="text.disabled" sx={{ px: 2 }}>
              {translate('pos.alerts.showing_count', {
                shown: Math.min(alerts.length, 10),
                total: alerts.length,
              })}
            </Typography>
          </Box>
        )}
      </Menu>
    </>
  )
}

export default AlertMenuButton
